import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { patientService } from '../services/patientService';
import { appointmentService } from '../services/appointmentService';
import { assessmentCaseService } from '../services/assessmentCaseService';
import { billingDocService } from '../services/billingDocService';
import { referralService } from '../services/referralService';
import { practiceExtrasService } from '../services/practiceExtrasService';

const Analysis = () => {
  const [stats, setStats] = useState({
    patients: 0,
    appointments: 0,
    upcoming: 0,
    assessmentCases: 0,
    scoredInstruments: 0,
    billingDrafts: 0,
    billingApproved: 0,
    referralsOpen: 0,
  });
  const [scoreRows, setScoreRows] = useState([]);
  const [outcomeSeries, setOutcomeSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [patients, appts, cases, billing, referrals, outcomes] = await Promise.all([
          patientService.getPatients({}),
          appointmentService.list(),
          assessmentCaseService.list(),
          billingDocService.list(),
          referralService.list(),
          practiceExtrasService.listOutcomes().catch(() => ({ data: [] })),
        ]);

        const patientList = Array.isArray(patients) ? patients : patients.data || [];
        const appointmentList = appts.data || [];
        const caseList = cases.data || [];
        const billingList = billing.data || [];
        const referralList = referrals.data || [];
        const outcomeList = outcomes.data || [];

        const now = Date.now();
        const upcoming = appointmentList.filter(
          (a) => a.startAt && new Date(a.startAt).getTime() >= now && !['cancelled', 'no_show'].includes(a.status),
        ).length;

        let scoredInstruments = 0;
        const rows = [];
        caseList.forEach((c) => {
          (c.instruments || []).forEach((inst) => {
            if (inst.scores && inst.scores.total != null) {
              scoredInstruments += 1;
              rows.push({
                caseTitle: c.title,
                patient: typeof c.patientId === 'object'
                  ? c.patientId.fullName || [c.patientId.firstName, c.patientId.lastName].filter(Boolean).join(' ')
                  : '—',
                instrument: inst.name,
                score: inst.scores.total,
                status: c.status,
              });
            }
          });
        });

        // Group outcomes by instrument for simple longitudinal bars
        const byInstrument = {};
        outcomeList.forEach((o) => {
          const key = o.instrumentName || 'Measure';
          if (!byInstrument[key]) byInstrument[key] = [];
          byInstrument[key].push(o);
        });
        const series = Object.entries(byInstrument).map(([name, points]) => ({
          name,
          points: points
            .slice()
            .sort((a, b) => new Date(a.measuredAt) - new Date(b.measuredAt))
            .slice(-12),
        }));

        setOutcomeSeries(series);
        setScoreRows(rows.slice(0, 50));
        setStats({
          patients: patientList.length,
          appointments: appointmentList.length,
          upcoming,
          assessmentCases: caseList.length,
          scoredInstruments,
          billingDrafts: billingList.filter((b) => b.status === 'draft').length,
          billingApproved: billingList.filter((b) => b.clinicianApproved).length,
          referralsOpen: referralList.filter(
            (r) => !['closed_won', 'closed_lost'].includes(r.stage),
          ).length,
        });
      } catch (e) {
        setError(e.response?.data?.message || e.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const cards = [
    { label: 'Patients', value: stats.patients },
    { label: 'Upcoming visits', value: stats.upcoming },
    { label: 'All appointments', value: stats.appointments },
    { label: 'Assessment cases', value: stats.assessmentCases },
    { label: 'Scored instruments', value: stats.scoredInstruments },
    { label: 'Open referrals', value: stats.referralsOpen },
    { label: 'Billing drafts', value: stats.billingDrafts },
    { label: 'Approved billing docs', value: stats.billingApproved },
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-poppins font-semibold text-primary mb-2">Analysis</h1>
          <p className="text-gray-500 mb-6">
            Practice snapshot and longitudinal score entries (manual scores only — no proprietary norms).
          </p>

          {loading && <p>Loading…</p>}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && !error && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {cards.map((c) => (
                  <div key={c.label} className="bg-white rounded-xl shadow p-5">
                    <div className="text-2xl font-semibold text-primary">{c.value}</div>
                    <div className="text-sm text-gray-500 mt-1">{c.label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl shadow overflow-x-auto mb-8">
                <div className="p-4 border-b font-medium">Longitudinal outcomes (manual scores)</div>
                {outcomeSeries.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500">
                    No outcome measures yet. Add them in Clinical Ops → Outcomes.
                  </p>
                ) : (
                  <div className="p-4 space-y-6">
                    {outcomeSeries.map((s) => {
                      const max = Math.max(...s.points.map((p) => Number(p.score) || 0), 1);
                      return (
                        <div key={s.name}>
                          <div className="text-sm font-medium mb-2">{s.name}</div>
                          <div className="flex items-end gap-1 h-24">
                            {s.points.map((p, i) => (
                              <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                                <div
                                  className="w-full bg-primary/80 rounded-t"
                                  style={{
                                    height: `${Math.max(8, ((Number(p.score) || 0) / max) * 100)}%`,
                                  }}
                                  title={`${p.score} on ${p.measuredAt ? new Date(p.measuredAt).toLocaleDateString() : ''}`}
                                />
                                <div className="text-[10px] text-gray-400 mt-1 truncate w-full text-center">
                                  {p.score}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl shadow overflow-x-auto">
                <div className="p-4 border-b font-medium">Recent instrument scores</div>
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3">Patient</th>
                      <th className="p-3">Case</th>
                      <th className="p-3">Instrument</th>
                      <th className="p-3">Score</th>
                      <th className="p-3">Case status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoreRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-6 text-center text-gray-500">
                          No scored instruments yet. Add scores from a patient assessment case.
                        </td>
                      </tr>
                    )}
                    {scoreRows.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-3">{r.patient}</td>
                        <td className="p-3">{r.caseTitle}</td>
                        <td className="p-3">{r.instrument}</td>
                        <td className="p-3">{r.score}</td>
                        <td className="p-3">{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Analysis;
